/**
 * Minimal i18n: a React Context with a `t()` function and a small JSON
 * dictionary. No external library — keeps the bundle tiny.
 *
 * Default language is Dutch (`nl`). User preference is persisted to
 * localStorage as `neurosophia.lang`. Switching is reactive across the
 * whole app via Context.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { TRANSLATIONS } from "@/lib/translations";

const STORAGE_KEY = "neurosophia.lang";
export const SUPPORTED_LANGS = ["nl", "en"];
export const DEFAULT_LANG = "nl";

const I18nContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (k) => k,
});

function readStoredLang() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
  } catch (e) {
    // localStorage can throw in SSR / private-browsing / cookies-disabled
    // contexts. Silently fall back to the default language — no UX impact.
  }
  return DEFAULT_LANG;
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(readStoredLang);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch (e) {
      // localStorage can throw in SSR / private-browsing / cookies-disabled
      // contexts. Persistence is a nice-to-have — the in-memory state still
      // drives the current session correctly.
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED_LANGS.includes(next)) setLangState(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = TRANSLATIONS[lang] || {};
      let value = dict[key];
      if (value == null) {
        // Fall back to English, then to the key itself.
        value = (TRANSLATIONS.en || {})[key] ?? key;
      }
      if (vars && typeof value === "string") {
        Object.entries(vars).forEach(([k, v]) => {
          value = value.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
        });
      }
      return value;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  return useContext(I18nContext);
}
